import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isArray, isEmpty } from 'lodash';

import postLiveAccount from 'Api/live-account/postLiveAccount';
import { useToast } from 'Store/toast';
import { useLoader } from 'Store/loader';
import { useLiveAccountState } from 'View/live-account/state';

import upsertLiveAccountPayload from './model/request/upsertLiveAccountPayload';

const usePostLiveAccount = ({ resetFormData }) => {
  const { t } = useTranslation('live-account');
  const queryClient = useQueryClient();
  const { onOpenToast } = useToast();
  const { onShowLoader, onResetLoader } = useLoader();
  const { updateState, resetState, resetFilterState } = useLiveAccountState();

  return useMutation({
    mutationFn: async (formData) => {
      onShowLoader({ variant: 'loader-primary' });

      const payload = upsertLiveAccountPayload(formData);

      return await postLiveAccount({ payload });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['live-account'] });

      onOpenToast({
        status: 'success',
        description: t('createSuccess'),
        duration: 5,
        alwaysShow: false,
      });

      resetState();
      resetFilterState();
      resetFormData();
    },
    onError: (error) => {
      const errorFields = error?.response?.data?.errorFields;
      const errorMessage = isArray(error?.response?.data?.errors) ? error?.response?.data?.errors[0]?.message : t('createErrorDescription');

      if (!isEmpty(errorFields)) return updateState({ errorFields });

      updateState({
        isOpenFormUpsert: false,
        errorFields: {},
        errorProps: {
          isOpen: true,
          type: 'error',
          title: t('createErrorTitle'),
          description: errorMessage,
          onClose: () => updateState({ errorProps: {} }),
          marginBottom: '12px',
        },
      });
    },
    onSettled: () => {
      onResetLoader();
    },
  });
};

export default usePostLiveAccount;